import { VaccinationAct as ProVaccinationAct } from "@syadem/kairos-pro-js";
import { VaccinationAct as TeamVaccinationAct } from "@syadem/kairos-team-js";
import { dayjs } from "../../../../utils/dayjs";

export interface EvcVaccinationPreview {
  id: number;
  nuvaCode: number;
  vaccineName: string;
  diseasesLabel: string;
  countryCode: string;
  performedOn: string;
  booster: boolean;
}

export interface HealthRecordVaccinationSource {
  ehrId: string;
  nuvaCode: number;
  performedOn: string;
}

export interface EvcVaccinationMatch extends EvcVaccinationPreview {
  evcVaccinationId: number;
  status: "exact" | "new";
}

export interface EvcVaccinationImportPlanItem extends EvcVaccinationMatch {
  selected: boolean;
  importable: boolean;
  vaccineId?: string;
  error?: "missing-vaccine" | "missing-health-record";
}

export function buildHealthRecordVaccinationSource(
  healthRecordVaccinations: (ProVaccinationAct | TeamVaccinationAct)[] | undefined,
  findVaccineById: (vaccineId: string) => { code: number } | undefined | null,
): HealthRecordVaccinationSource[] {
  if (!healthRecordVaccinations || healthRecordVaccinations.length === 0) {
    return [];
  }

  return healthRecordVaccinations
    .map((act) => {
      const vaccine = findVaccineById(act.vaccineId);
      if (!vaccine || !act.performedOn) return null;

      return {
        ehrId: act.id,
        nuvaCode: vaccine.code,
        performedOn: dayjs.utc(act.performedOn).format("YYYY-MM-DD"),
      };
    })
    .filter((value): value is HealthRecordVaccinationSource => value !== null);
}

export function reconcileEvcVaccinations(
  evcVaccinations: EvcVaccinationPreview[],
  healthRecordVaccinations: HealthRecordVaccinationSource[],
): EvcVaccinationMatch[] {
  const healthRecordIndex = new Set(
    healthRecordVaccinations.map((vaccination) =>
      buildVaccinationMatchKey(vaccination.nuvaCode, vaccination.performedOn),
    ),
  );

  return evcVaccinations.map((vaccination) => {
    const status = healthRecordIndex.has(buildVaccinationMatchKey(vaccination.nuvaCode, vaccination.performedOn))
      ? "exact"
      : "new";

    return {
      ...vaccination,
      evcVaccinationId: vaccination.id,
      status,
    };
  });
}

export function buildEvcVaccinationImportPlan({
  matches,
  selectedById,
  findVaccineByNuvaCode,
  healthRecordId,
}: {
  matches: EvcVaccinationMatch[];
  selectedById: Record<number, boolean>;
  findVaccineByNuvaCode: (nuvaCode: number) => { id: string } | undefined | null;
  healthRecordId?: string | null;
}): EvcVaccinationImportPlanItem[] {
  return matches.map((match) => {
    const selected = match.status === "new" && !!selectedById[match.evcVaccinationId];
    const vaccine = findVaccineByNuvaCode(match.nuvaCode);

    if (!vaccine) {
      return {
        ...match,
        selected,
        importable: false,
        error: "missing-vaccine",
      };
    }

    if (!healthRecordId) {
      return {
        ...match,
        selected,
        importable: false,
        vaccineId: vaccine.id,
        error: "missing-health-record",
      };
    }

    return {
      ...match,
      selected,
      importable: true,
      vaccineId: vaccine.id,
    };
  });
}

function buildVaccinationMatchKey(nuvaCode: number, performedOn: string): string {
  return `${nuvaCode}|${performedOn}`;
}
